/**
 * The activation flow — the panel's daily primary action.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ **Why this has no type-to-confirm, stated here rather than inherited**│
 * │                                                                      │
 * │ Activation is billable, so it needs a safeguard. It is also the most  │
 * │ *used* control in the product, and a typed phrase on the most-used    │
 * │ control trains people to type through it — which devalues the gesture │
 * │ exactly where it matters, on swap and disconnect.                     │
 * │                                                                      │
 * │ So the safeguard here is **information, not friction**: the plan, its │
 * │ price, the per-SIM cost, the total, and every MSISDN, all visible on  │
 * │ one screen before Activate is pressable. An operator who reads this   │
 * │ page cannot be surprised by the bill.                                 │
 * │                                                                      │
 * │ Previously this decision existed only as `destructive: false` in the  │
 * │ action registry — true, but nowhere expressed. It is expressed here.  │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * The safety properties Phase 05 established still hold and are not restated
 * loosely: **every MSISDN is listed and never elided**, the selection cannot
 * exceed what the user has seen, the submit carries an idempotency key that is
 * re-minted when the payload changes, and the resulting job is tracked to a
 * terminal state where `EXPIRED` never reads as failure.
 */

import type { PlanSummary, SimSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { useAuth } from '../../../auth/AuthProvider.js';
import { Badge } from '../../../components/Badge.js';
import { Button } from '../../../components/Button.js';
import { ErrorState } from '../../../components/ErrorState.js';
import { Field } from '../../../components/Field.js';
import { Checkbox, Select } from '../../../components/Input.js';
import { PageHeader } from '../../../components/PageHeader.js';
import { Skeleton } from '../../../components/Skeleton.js';
import { apiRequest } from '../../../lib/apiClient.js';
import { useJobDrawer } from '../../../lib/JobDrawerContext.js';
import { useSelection } from '../../inventory/selection.js';
import { usePlans } from '../../inventory/useInventory.js';
import { SIM_ACTIONS, isEligible } from '../actions/actionRegistry.js';
import { useSimAction } from '../actions/useSimAction.js';
import { simStatusLabel, simStatusTone } from '../statusTone.js';
import { refsNeedingReveal, useRevealedSims } from '../useRevealedSims.js';

import { EligibleSimPicker } from './EligibleSimPicker.js';

const ACTION = SIM_ACTIONS.activate;

/**
 * Formats a per-SIM price into a total.
 *
 * The upstream sends price as a string and we do not know its currency, so the
 * total is rendered in the same units the plan quoted and nothing is invented.
 * If the price is not a number we say so rather than showing `NaN` or silently
 * dropping the line — an operator deciding on a bill needs to know we could not
 * compute it.
 */
export function priceSummary(
  plan: PlanSummary | undefined,
  count: number,
): { each: string; total: string | null } {
  if (!plan?.price) return { each: 'not quoted', total: null };
  const each = plan.price;
  /**
   * A price of "on request" strips to an empty string, and `Number('')` is
   * **0** — which would render a total of `0.00` and tell the operator this
   * activation is free. An unparseable price must say so, not claim a number.
   */
  const stripped = plan.price.replace(/[^\d.]/g, '');
  const numeric = plan.priceValue ?? (stripped === '' ? Number.NaN : Number(stripped));
  if (!Number.isFinite(numeric)) return { each, total: null };
  return { each, total: (numeric * count).toFixed(2) };
}

/**
 * Activating **one** SIM, named by the URL — the estate row's Activate action
 * and the SIM detail page's.
 *
 * This exists because the row action did not work. It linked to
 * `/sims/activate?sim=<ref>` and nothing read that parameter, so an operator
 * who clicked Activate on one SIM was dropped on the picker and asked to find
 * it again among 11,993. Reproduced in the live panel before this fix: the
 * heading said "Activate SIMs" and the body said "Pick the SIMs to activate".
 *
 * The shape is `SwapFlow`'s, deliberately: a per-SIM route that takes its
 * subject from the path and fetches it, rather than a query parameter read out
 * of shared selection state. It shares the `['sim', ref]` query key with the
 * detail page, so arriving from there costs no request.
 */
export function SingleSimActivation(): ReactElement {
  const { simRef } = useParams<{ simRef: string }>();
  const sim = useQuery<SimSummary>({
    queryKey: ['sim', simRef],
    queryFn: () => apiRequest<SimSummary>(`/sims/${simRef ?? ''}`),
    enabled: Boolean(simRef),
  });

  if (sim.isPending) {
    return (
      <>
        <Skeleton className="mb-[var(--space-6)] h-12 w-64" />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }
  if (sim.isError || !sim.data) {
    return <ErrorState error={sim.error} onRetry={() => void sim.refetch()} />;
  }

  return <ActivationFlow sim={sim.data} />;
}

/**
 * `sim` present means "this one SIM, from the URL"; absent means the
 * multi-select flow driven by the estate's selection. The prop is passed rather
 * than written into `useSelection()` on mount: seeding shared state as a side
 * effect of rendering would leak this SIM into the estate's checkboxes and into
 * any other flow the operator opened next.
 */
export function ActivationFlow({ sim }: { sim?: SimSummary } = {}): ReactElement {
  const selection = useSelection();
  // When the tenant has a default plan the operator never chooses one: no
  // picker, no pricing, and the body carries no planCode — the server fills it
  // in (sims.service.ts withResolvedPlan). Plans are then never fetched at all,
  // so a tenant without the plans entitlement can still activate.
  const { tenant } = useAuth();
  const usesDefaultPlan = tenant?.hasDefaultPlan === true;
  const plans = usePlans({ enabled: !usesDefaultPlan });
  const navigate = useNavigate();
  const { track } = useJobDrawer();
  const { submit, isSubmitting, error } = useSimAction(ACTION);

  const [planCode, setPlanCode] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  /**
   * §A PR-5 — the pick/review two-step.
   *
   * `intent` records an *explicit* gesture only; the step itself is derived,
   * so the flow stays reactive to the selection rather than freezing its
   * arrival state:
   *
   *   * arriving WITH a selection (the estate path) → `intent` is null and
   *     the count is positive → review directly, exactly as before;
   *   * arriving with NOTHING (the dashboard tile — previously the dead end
   *     this PR exists to fix) → the picker renders instead of an empty
   *     state, and the picker's `onPick` pins `intent` to `'pick'` so the
   *     first checked row does not yank the user into review mid-selection;
   *   * "Review" clears the pin; "Change selection" sets it again.
   */
  const [intent, setIntent] = useState<'pick' | null>(null);

  /*
    A SIM named by the URL is the whole subject: it is not a selection the
    operator can change here, so there is no picker step to fall back to and
    `intent` does not apply. An ineligible one still reaches review — `excluded`
    below says why and `canSubmit` stays false, which is the honest answer to
    "activate this" when it cannot be activated.
  */
  const selected = sim ? [sim] : selection.selectedSims();
  const step: 'pick' | 'review' =
    sim === undefined && (intent === 'pick' || selected.length === 0) ? 'pick' : 'review';
  const eligible = useMemo(() => selected.filter((sim) => isEligible(ACTION, sim)), [selected]);
  const excluded = useMemo(() => selected.filter((sim) => !isEligible(ACTION, sim)), [selected]);

  const plan = plans.data?.find((p) => p.planCode === planCode);
  const price = priceSummary(plan, eligible.length);

  /*
    The consequence sentence is the safeguard that stands in for a typed
    confirmation, so it has to describe what will actually happen. The registry
    copy names the plan the operator chose; on a default-plan tenant there is no
    such choice, and promising one would be a lie in the single most consequential
    sentence on the screen.
  */
  const consequence = usesDefaultPlan
    ? 'These SIMs start carrying traffic, and billing begins.'
    : ACTION.consequence;

  const canSubmit = (usesDefaultPlan || Boolean(planCode)) && eligible.length > 0 && !isSubmitting;

  // PR-8: the selection carries the estate's masked rows (§B4), and the
  // activate body needs real MSISDNs — the fetch starts at mount and the
  // submit awaits it, so a fast confirm cannot post masked values.
  const { ensureRevealed } = useRevealedSims(refsNeedingReveal(eligible));

  const run = async (): Promise<void> => {
    try {
      const revealed = await ensureRevealed();
      const job = await submit({
        items: eligible.map((sim) => {
          const ids = revealed.get(sim.simRef) ?? { msisdn: sim.msisdn, iccid: sim.iccid };
          return {
            msisdn: ids.msisdn ?? '',
            ...(ids.iccid ? { iccid: ids.iccid } : {}),
            ...(usesDefaultPlan ? {} : { planCode: planCode ?? '' }),
          };
        }),
        ...(consent ? { consent } : {}),
      });
      // §05.8: the drawer reports progress; no success toast, because at this
      // point nothing has succeeded — the network has only accepted the work.
      track(job.ref);
      selection.clear();
      void navigate('/sims');
    } catch {
      // `error` renders in place; the form keeps its idempotency key so a retry
      // of the same payload cannot become a second activation.
    }
  };

  if (step === 'pick') {
    // The picker, not a dead end: this route is the dashboard tile's landing
    // and must be able to *start* the work, not just receive it (§A PR-5).
    return (
      <>
        <PageHeader
          title="Activate SIMs"
          description={
            usesDefaultPlan
              ? 'Pick the SIMs to activate, then review them.'
              : 'Pick the SIMs to activate, then review the plan and price.'
          }
        />
        <div className="flex flex-col gap-4">
          <EligibleSimPicker action={ACTION} onPick={() => setIntent('pick')} />
          <div className="flex items-center gap-3">
            <Button disabled={selected.length === 0} onClick={() => setIntent(null)}>
              {selected.length > 0
                ? `Review ${selected.length} ${selected.length === 1 ? 'SIM' : 'SIMs'}`
                : 'Review selection'}
            </Button>
            <Link to="/sims" className="text-[length:var(--text-sm)] text-primary underline">
              Or choose from the estate
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Activate SIMs"
        description={
          usesDefaultPlan
            ? 'Review what you are activating, then confirm.'
            : 'Review what you are activating, choose a plan, then confirm.'
        }
      />

      <div className="flex flex-col gap-6 lg:max-w-3xl">
        {/* 1 · How many. The count is what people sanity-check, so it leads. */}
        <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
          <p className="text-[length:var(--text-xs)] uppercase tracking-[0.04em] text-muted">
            Activating
          </p>
          <p
            className="mt-1 text-[length:var(--text-2xl)] font-semibold tabular-nums text-body"
            data-testid="activation-count"
          >
            {eligible.length} {eligible.length === 1 ? 'SIM' : 'SIMs'}
          </p>

          {excluded.length > 0 ? (
            <div
              className="mt-3 text-[length:var(--text-xs)] text-muted"
              data-testid="activation-excluded"
            >
              <p className="font-medium">
                {excluded.length} selected {excluded.length === 1 ? 'SIM is' : 'SIMs are'} not
                eligible and will not be activated:
              </p>
              <ul className="mt-1 max-h-24 overflow-y-auto font-[family-name:var(--font-mono)]">
                {excluded.map((sim) => (
                  <li key={sim.simRef}>
                    {sim.msisdn ?? sim.simRef} — {simStatusLabel(sim.status)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        {/* 2 · Which SIMs. Every one, never elided — an operator must be able to
            see that the SIM they did not mean to select is not in the list. */}
        <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
          <h2 className="text-[length:var(--text-lg)] font-medium text-body">These SIMs</h2>
          <ul
            data-testid="activation-targets"
            className="mt-2 max-h-56 overflow-y-auto rounded-[var(--radius-sm)] border border-line bg-surface-alt"
          >
            {eligible.map((sim) => (
              <li
                key={sim.simRef}
                className="flex items-center justify-between gap-3 border-b border-line px-3 py-1.5 last:border-b-0"
              >
                <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-body">
                  {sim.msisdn ?? sim.simRef}
                </span>
                <Badge tone={simStatusTone(sim.status)}>{simStatusLabel(sim.status)}</Badge>
              </li>
            ))}
          </ul>
        </section>

        {/* 3 · What it costs — omitted entirely when the tenant activates on a
            default plan. The safeguard does not disappear with it: the count and
            the full MSISDN list above still state exactly what is about to
            happen, which is what a typed confirmation phrase would have bought. */}
        <section
          hidden={usesDefaultPlan}
          className="rounded-[var(--radius-md)] border border-line bg-surface p-4"
        >
          <h2 className="text-[length:var(--text-lg)] font-medium text-body">Plan and price</h2>

          {plans.isPending ? <Skeleton className="mt-3 h-9 w-full" /> : null}
          {plans.isError ? (
            <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
          ) : null}

          {plans.data ? (
            <div className="mt-3 flex flex-col gap-3">
              <Field label="Plan" required>
                {(props) => (
                  <Select
                    {...props}
                    value={planCode ?? ''}
                    onChange={(e) => setPlanCode(e.target.value || null)}
                  >
                    {/* No ellipsis — see PlanPicker: '…' is the elision
                        guard's trip-wire, so placeholders may not use it. */}
                    <option value="">Choose a plan</option>
                    {plans.data.map((p) => (
                      <option key={p.planCode} value={p.planCode}>
                        {p.planName ?? p.planCode}
                        {p.price ? ` — ${p.price}` : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {plan ? (
                <dl
                  data-testid="activation-summary"
                  className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-[var(--radius-sm)] border border-line bg-surface-alt p-3 text-[length:var(--text-sm)]"
                >
                  <dt className="text-muted">Plan</dt>
                  <dd className="text-body">{plan.planName ?? plan.planCode}</dd>

                  <dt className="text-muted">Price per SIM</dt>
                  <dd className="tabular-nums text-body">{price.each}</dd>

                  <dt className="text-muted">SIMs</dt>
                  <dd className="tabular-nums text-body">{eligible.length}</dd>

                  <dt className="font-medium text-body">Total</dt>
                  <dd className="font-medium tabular-nums text-body">
                    {price.total ?? 'We could not calculate this from the quoted price.'}
                  </dd>
                </dl>
              ) : (
                <p className="text-[length:var(--text-sm)] text-muted">
                  Choose a plan to see what this will cost.
                </p>
              )}
            </div>
          ) : null}
        </section>

        {error ? <ErrorState error={error} /> : null}

        {/* 4 · Commit. No typed phrase: the information above is the safeguard,
            and a phrase on the most-used control teaches people to type through
            it — which is where swap and disconnect lose their protection. */}
        <section className="flex flex-col gap-3">
          {ACTION.offersConsent ? (
            <label className="flex items-start gap-2 text-[length:var(--text-sm)] text-body">
              <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>I confirm the customer has agreed to this activation.</span>
            </label>
          ) : null}

          <p className="text-[length:var(--text-sm)] text-muted">{consequence}</p>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void navigate('/sims')}>
              Cancel
            </Button>
            {/* Back to the picker with the selection intact — reviewing is
                not committing, and changing your mind must not cost the work. */}
            <Button variant="secondary" onClick={() => setIntent('pick')}>
              Change selection
            </Button>
            <Button loading={isSubmitting} disabled={!canSubmit} onClick={() => void run()}>
              {plan && price.total
                ? `Activate ${eligible.length} ${eligible.length === 1 ? 'SIM' : 'SIMs'} · ${price.total}`
                : `Activate ${eligible.length} ${eligible.length === 1 ? 'SIM' : 'SIMs'}`}
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
